import React, { useState, useRef, useEffect, useMemo } from "react";
import { Home, Download, Menu, Gift, X, HelpCircle, User, Heart, MessageCircle, Headphones } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import { CasinoSidebarNew } from "./CasinoSidebarNew";
import { RedeemModal } from "./RedeemModal";
import { useGetCoins } from "@/contexts/GetCoinsContext";
import { useDailyBonus } from "@/contexts/DailyBonusContext";
import { useVerification } from "@/contexts/VerificationContext";
import { useJoiningBonus } from "@/contexts/JoiningBonusContext";
import { useWelcomeOffer } from "@/contexts/WelcomeOfferContext";
import { useChatOptional } from "@/contexts/ChatContext";
import { useSupportChatVisibilityOptional } from "@/contexts/SupportChatVisibilityContext";
import goldMachineLogo from "@/assets/Gold Machine Logo 1.png";
import { useLogoSrc } from "@/hooks/useLogoSrc";
import { useLiveCategoriesQuery } from "@/hooks/queries/useLiveCategoriesQuery";

// Custom SVG Icons for Quick Actions (matching sidebar style exactly)
const ForYouIcon = ({ className }: { className?: string }) => (
  <Heart className={className} />
);

const EndlessReelsIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    {/* Slot machine frame */}
    <rect x="2" y="4" width="20" height="16" rx="2" />
    {/* Three reel windows */}
    <rect x="4.5" y="7" width="4" height="10" rx="1" fill="currentColor" opacity="0.1" />
    <rect x="10" y="7" width="4" height="10" rx="1" fill="currentColor" opacity="0.1" />
    <rect x="15.5" y="7" width="4" height="10" rx="1" fill="currentColor" opacity="0.1" />
    {/* Reel symbols — center row (winning line) */}
    <circle cx="6.5" cy="12" r="1.5" fill="currentColor" opacity="0.6" />
    <path d="M11 10.5l1 1.5 1-1.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <circle cx="17.5" cy="12" r="1.5" fill="currentColor" opacity="0.6" />
    {/* Win line */}
    <line x1="3" y1="12" x2="4.5" y2="12" strokeWidth="1" opacity="0.5" />
    <line x1="19.5" y1="12" x2="21" y2="12" strokeWidth="1" opacity="0.5" />
  </svg>
);

const LiveCasinoIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="2" y="6" width="14" height="12" rx="2" />
    <path d="M18 10l4-2v8l-4-2v-4z" />
    <circle cx="9" cy="12" r="2" />
    <circle cx="20" cy="4" r="2" fill="#ef4444" stroke="#ef4444" />
  </svg>
);

const JackpotIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    <circle cx="12" cy="12" r="4" />
    <text x="9.5" y="14.5" fontSize="6" fill="currentColor" fontWeight="bold">$</text>
  </svg>
);

const LoyaltyIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 2L15 8.5L22 9.5L17 14.5L18 22L12 18.5L6 22L7 14.5L2 9.5L9 8.5L12 2Z" />
    <circle cx="12" cy="12" r="3" fill="currentColor" opacity="0.3" />
  </svg>
);

const ReferFriendsIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="8" cy="8" r="4" />
    <path d="M2 20v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
    <path d="M16 11h6M19 8v6" />
  </svg>
);

const VaultIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <circle cx="12" cy="13" r="4" />
    <circle cx="12" cy="13" r="1.5" fill="currentColor" />
    <line x1="12" y1="9" x2="12" y2="10" />
    <line x1="12" y1="16" x2="12" y2="17" />
    <line x1="8" y1="13" x2="9" y2="13" />
    <line x1="15" y1="13" x2="16" y2="13" />
    <rect x="8" y="2" width="8" height="3" rx="1" />
  </svg>
);

const DailyBonusIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <path d="M12 14l1.5 3 3.5.5-2.5 2.5.5 3.5-3-1.5-3 1.5.5-3.5-2.5-2.5 3.5-.5z" fill="currentColor" opacity="0.3" />
  </svg>
);

const PromotionsIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M19 4H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
    <path d="M12 8v8" />
    <path d="M8 12h8" />
    <circle cx="17" cy="7" r="3" fill="#ef4444" stroke="#ef4444" />
    <text x="15.5" y="8.5" fontSize="4" fill="white" fontWeight="bold">%</text>
  </svg>
);

const GetCoinsIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    {/* Stack of coins */}
    <ellipse cx="12" cy="8" rx="7" ry="3" />
    <path d="M5 8v4c0 1.66 3.13 3 7 3s7-1.34 7-3V8" />
    <path d="M5 12v4c0 1.66 3.13 3 7 3s7-1.34 7-3v-4" />
  </svg>
);

const RedeemIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect x="3" y="8" width="18" height="12" rx="2" />
    <path d="M12 8V5a3 3 0 0 0-3-3h0a3 3 0 0 0-3 3v0" />
    <path d="M18 8V5a3 3 0 0 0-3-3h0a3 3 0 0 0-3 3v3" />
    <line x1="12" y1="8" x2="12" y2="20" />
    <line x1="3" y1="12" x2="21" y2="12" />
  </svg>
);

// Detect if app is running as installed PWA - computed synchronously to avoid flicker
const getIsPWA = (): boolean => {
  // Check for standalone display mode (Android/Desktop PWA)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  // Check for iOS Safari standalone mode
  const isIOSStandalone = (window.navigator as any).standalone === true;
  return isStandalone || isIOSStandalone;
};

// Cache the PWA state at module level to prevent re-computation and flicker on navigation
let cachedIsPWA: boolean | null = null;
const getCachedIsPWA = (): boolean => {
  if (cachedIsPWA === null) {
    cachedIsPWA = getIsPWA();
  }
  return cachedIsPWA;
};

export const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCurrency } = useTheme();
  const logoSrc = useLogoSrc();
  const { isGetCoinsModalOpen, openGetCoinsModal } = useGetCoins();
  const { showDailyBonusModal, openDailyBonusModal } = useDailyBonus();
  const { verificationStatus } = useVerification();
  const { showJoiningBonusModal } = useJoiningBonus();
  const { showWelcomeOfferModal } = useWelcomeOffer();
  const chatContext = useChatOptional();
  const supportChatContext = useSupportChatVisibilityOptional();
  const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMenuClosing, setIsMenuClosing] = useState(false);
  const [isQuickActionsOpen, setIsQuickActionsOpen] = useState(false);
  
  // Swipe-to-close state for menu
  const [menuDragY, setMenuDragY] = useState(0);
  const [isMenuDragging, setIsMenuDragging] = useState(false);
  const [isDragClosing, setIsDragClosing] = useState(false);
  const [isMenuEntered, setIsMenuEntered] = useState(false);
  const menuDragStartY = useRef(0);
  const menuSheetRef = useRef<HTMLDivElement>(null);

  // Animated close handler (for X button / backdrop tap)
  const closeMenu = () => {
    setIsMenuClosing(true);
    setIsMenuEntered(false);
    setTimeout(() => {
      setIsMenuOpen(false);
      setIsMenuClosing(false);
      setIsDragClosing(false);
      setMenuDragY(0);
    }, 300);
  };

  // Instant close (no animation) - used when tapping menu items / opening modals
  const closeMenuInstant = () => {
    setIsMenuOpen(false);
    setIsMenuClosing(false);
    setIsMenuEntered(false);
    setIsDragClosing(false);
    setMenuDragY(0);
  };

  // Drag-initiated close: continues the drag momentum into a slide-out
  const dragCloseMenu = () => {
    setIsDragClosing(true);
    setIsMenuClosing(true);
    setIsMenuEntered(false);
    setTimeout(() => {
      setIsMenuOpen(false);
      setIsMenuClosing(false);
      setIsDragClosing(false);
      setMenuDragY(0);
    }, 300);
  };

  // Listen for enter animation end so we can remove the animation class
  // and let inline transform styles take over for drag
  useEffect(() => {
    const el = menuSheetRef.current;
    if (!el || !isMenuOpen) return;
    const onEnd = (e: AnimationEvent) => {
      if (e.animationName === 'menu-sheet-slide-up') {
        setIsMenuEntered(true);
      }
    };
    el.addEventListener('animationend', onEnd);
    return () => el.removeEventListener('animationend', onEnd);
  }, [isMenuOpen]);

  // Swipe-to-close handlers for menu
  const handleMenuDragStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    menuDragStartY.current = e.touches[0].clientY;
    setIsMenuDragging(true);
  };

  const handleMenuDragMove = (e: React.TouchEvent) => {
    if (!isMenuDragging) return;
    e.preventDefault();
    e.stopPropagation();
    const deltaY = e.touches[0].clientY - menuDragStartY.current;
    if (deltaY > 0) {
      setMenuDragY(deltaY);
    }
  };

  const handleMenuDragEnd = () => {
    if (!isMenuDragging) return;
    if (menuDragY > 100) {
      dragCloseMenu();
    } else {
      setMenuDragY(0);
    }
    setIsMenuDragging(false);
    menuDragStartY.current = 0;
  };

  // Reset drag state when menu closes
  useEffect(() => {
    if (!isMenuOpen) {
      setMenuDragY(0);
      setIsMenuDragging(false);
      setIsDragClosing(false);
      setIsMenuEntered(false);
    }
  }, [isMenuOpen]);

  // Lock body scroll when menu sheet is open to prevent background scrolling
  useEffect(() => {
    if (isMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMenuOpen]);

  // Close menu and mobile chat instantly when any modal/popup opens (including auto-triggered ones like welcome offer)
  const anyModalOpen = isGetCoinsModalOpen || isRedeemModalOpen || showDailyBonusModal || showJoiningBonusModal || showWelcomeOfferModal;
  useEffect(() => {
    if (anyModalOpen) {
      if (isMenuOpen) closeMenuInstant();
      if (chatContext?.isMobileOpen) chatContext.closeMobileChat();
    }
  }, [anyModalOpen]);

  // Use cached PWA state to prevent flicker on navigation
  const isPWA = useMemo(() => getCachedIsPWA(), []);
  
  // Use refs to track current state for reliable toggling
  const isMenuOpenRef = useRef(isMenuOpen);
  
  // Keep refs in sync with state
  useEffect(() => {
    isMenuOpenRef.current = isMenuOpen;
  }, [isMenuOpen]);

  // Close quick actions when navigating
  useEffect(() => {
    setIsQuickActionsOpen(false);
  }, [location.pathname]);

  // Check if home page is active (matches "/" or "/home")
  const isActive = (path: string) => {
    if (path === "/home") {
      return location.pathname === "/" || location.pathname === "/home";
    }
    return location.pathname === path;
  };

  const handleMenuClick = () => {
    // Close quick actions if open before toggling menu
    if (isQuickActionsOpen) {
      setIsQuickActionsOpen(false);
    }
    // Toggle Menu using ref for reliable state check
    if (isMenuOpenRef.current) {
      closeMenu();
    } else {
      // Close mobile chat and support chat before opening menu
      if (chatContext?.isMobileOpen) chatContext.closeMobileChat();
      if (supportChatContext?.isOpen) supportChatContext.closeChat();
      setIsMenuOpen(true);
    }
  };

  const handleLogoClick = () => {
    // If the menu sheet is open, close it instantly before toggling quick actions
    if (isMenuOpenRef.current) {
      closeMenuInstant();
    }
    // Close mobile chat and support chat when opening quick actions
    if (!isQuickActionsOpen) {
      if (chatContext?.isMobileOpen) chatContext.closeMobileChat();
      if (supportChatContext?.isOpen) supportChatContext.closeChat();
    }
    setIsQuickActionsOpen(prev => !prev);
  };

  // Close quick actions and also close the menu sheet if it's open
  const closeQuickActions = () => {
    setIsQuickActionsOpen(false);
    if (isMenuOpenRef.current) {
      closeMenuInstant();
    }
  };

  const { data: liveData } = useLiveCategoriesQuery();
  const liveCasinoEnabled = liveData?.enabled ?? false;

  // Quick action items organized by priority with different sizes
  // Size: large (most used), medium (high priority), small (secondary)
  const mostUsedItems = useMemo(() => [
    ...(liveCasinoEnabled ? [{ icon: LiveCasinoIcon, label: "Live" as const, color: "from-red-500 to-red-700" as const, action: () => { closeQuickActions(); navigate("/live-experience"); }, showIn: "both" as const }] : []),
    { icon: EndlessReelsIcon, label: "Reels", color: "from-purple-500 to-purple-700", action: () => { closeQuickActions(); navigate("/endless-reels"); }, showIn: "both" },
    { icon: RedeemIcon, label: "Redeem", color: "from-violet-500 to-violet-700", action: () => { closeQuickActions(); requestAnimationFrame(() => setIsRedeemModalOpen(true)); }, showIn: "both" },
    { icon: GetCoinsIcon, label: "Get Coins", color: "from-emerald-500 to-emerald-700", action: () => { closeQuickActions(); requestAnimationFrame(() => openGetCoinsModal()); }, showIn: "both" },
  ], [liveCasinoEnabled, navigate]);

  const highPriorityItems = [
    { icon: ForYouIcon, label: "For You", color: "from-pink-500 to-pink-700", action: () => { closeQuickActions(); navigate("/for-you"); }, showIn: "both" },
    ...(chatContext ? [{ icon: MessageCircle, label: "Social Chat", color: "from-sky-500 to-sky-700", action: () => { closeQuickActions(); requestAnimationFrame(() => chatContext.openMobileChat()); }, showIn: "both" }] : []),
    { icon: ReferFriendsIcon, label: "Refer", color: "from-teal-500 to-teal-700", action: () => { closeQuickActions(); navigate("/refer-friends"); }, showIn: "web" }, // Hidden in PWA (has Refer button)
    { icon: JackpotIcon, label: "Jackpot", color: "from-amber-500 to-amber-700", action: () => { closeQuickActions(); navigate("/jackpot"); }, showIn: "both" },
    ...(supportChatContext ? [{ icon: Headphones, label: "Support", color: "from-indigo-500 to-indigo-700", action: () => { closeQuickActions(); requestAnimationFrame(() => supportChatContext.openChat()); }, showIn: "pwa" }] : []), // PWA only - Support Chat
    // Hide Profile from quick actions after data verification is completed (accessible via Menu)
    ...(!verificationStatus.isDataVerified ? [{ icon: User, label: "Profile", color: "from-blue-500 to-blue-700", action: () => { closeQuickActions(); navigate("/profile"); }, showIn: "pwa" }] : []),
  ];

  const secondaryItems = [
    // Hide Profile from quick actions after data verification is completed (accessible via Menu)
    ...(!verificationStatus.isDataVerified ? [{ icon: User, label: "Profile", color: "from-blue-500 to-blue-700", action: () => { closeQuickActions(); navigate("/profile"); }, showIn: "web" }] : []),
  ];

  // Filter items based on PWA status
  const filterItems = (items: typeof mostUsedItems) => items.filter(item => {
    if (item.showIn === "both") return true;
    if (item.showIn === "web" && !isPWA) return true;
    if (item.showIn === "pwa" && isPWA) return true;
    return false;
  });

  const filteredMostUsed = filterItems(mostUsedItems);
  const filteredHighPriority = filterItems(highPriorityItems);
  const filteredSecondary = filterItems(secondaryItems);

  // Per-icon notification counts (for quick actions panel)
  const getItemNotificationCount = (_label: string): number => {
    return 0;
  };

  const totalNotificationCount = 0;

  // Show "Refer" if running as installed PWA, otherwise show "Install"
  const fourthNavItem = isPWA
    ? { icon: Gift, label: "Refer", path: "/refer-friends", action: () => navigate("/refer-friends") }
    : { icon: Download, label: "Install", path: "/install-app", action: () => navigate("/install-app") };

  // Helper to close all overlays (quick actions, menu, mobile chat, and support chat) before navigating
  const closeAllOverlaysAndNavigate = (path: string) => {
    if (isQuickActionsOpen) setIsQuickActionsOpen(false);
    if (isMenuOpenRef.current) closeMenuInstant();
    if (chatContext?.isMobileOpen) chatContext.closeMobileChat();
    if (supportChatContext?.isOpen) supportChatContext.closeChat();
    navigate(path);
  };

  // Left side items (before logo)
  const leftNavItems = [
    { icon: Home, label: "Lobby", path: "/home", action: () => closeAllOverlaysAndNavigate("/home") },
    { icon: LoyaltyIcon, label: "VIP", path: "/loyalty-club", action: () => closeAllOverlaysAndNavigate("/loyalty-club") },
  ];

  // Right side items (after logo)
  const rightNavItems = [
    { 
      icon: fourthNavItem.icon, 
      label: fourthNavItem.label, 
      path: fourthNavItem.path, 
      action: () => closeAllOverlaysAndNavigate(fourthNavItem.path) 
    },
    { icon: Menu, label: "Menu", path: null, action: handleMenuClick },
  ];

  return (
    <>
    {/* Quick Actions Overlay */}
    {isQuickActionsOpen && (
      <div 
        className="lg:hidden fixed inset-0 z-[240] bg-black/70 backdrop-blur-sm transition-opacity duration-200"
        onClick={() => setIsQuickActionsOpen(false)}
      />
    )}

    {/* Quick Actions Menu - Tiered layout with different sizes */}
    <div className={`lg:hidden fixed bottom-[calc(5rem+env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 z-[245] transition-all duration-300 ease-out ${
      isQuickActionsOpen 
        ? 'opacity-100 translate-y-0 pointer-events-auto' 
        : 'opacity-0 translate-y-8 pointer-events-none'
    }`}>
      <div className="relative flex flex-col items-center gap-3 pb-4">
        {/* Most Used - Large icons */}
        <div className="flex items-center justify-center gap-3">
          {filteredMostUsed.map((item, index) => {
            const count = getItemNotificationCount(item.label);
            return (
              <button
                key={index}
                onClick={item.action}
                className="flex flex-col items-center gap-1.5 transition-all duration-200"
              >
                <div className={`relative w-[72px] h-[72px] rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-lg shadow-black/30 active:scale-95 transition-transform`}>
                  <item.icon className="w-9 h-9 text-white" />
                  {count > 0 && (
                    <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-full bg-white text-destructive text-[11px] font-bold leading-none shadow-md border-2 border-black/20">
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </div>
                <span className="text-[11px] font-medium text-white/90 text-center max-w-[75px] leading-tight line-clamp-2">
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* High Priority - Medium icons */}
        <div className="flex items-center justify-center gap-2.5">
          {filteredHighPriority.map((item, index) => {
            const count = getItemNotificationCount(item.label);
            return (
              <button
                key={index}
                onClick={item.action}
                className="flex flex-col items-center gap-1 transition-all duration-200"
              >
                <div className={`relative w-14 h-14 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center shadow-md shadow-black/25 active:scale-95 transition-transform`}>
                  <item.icon className="w-7 h-7 text-white" />
                  {count > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white text-destructive text-[10px] font-bold leading-none shadow-md border-2 border-black/20">
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </div>
                <span className="text-[10px] font-medium text-white/80 text-center max-w-[60px] leading-tight line-clamp-2">
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Secondary - Small icons */}
        <div className="flex items-center justify-center gap-2">
          {filteredSecondary.map((item, index) => {
            const count = getItemNotificationCount(item.label);
            return (
              <button
                key={index}
                onClick={item.action}
                className="flex flex-col items-center gap-0.5 transition-all duration-200"
              >
                <div className={`relative w-12 h-12 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center shadow-md shadow-black/20 active:scale-95 transition-transform`}>
                  <item.icon className="w-6 h-6 text-white" />
                  {count > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-[16px] px-0.5 rounded-full bg-white text-destructive text-[9px] font-bold leading-none shadow-md border border-black/20">
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </div>
                <span className="text-[9px] font-medium text-white/70 text-center max-w-[55px] leading-tight line-clamp-2">
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>

    {/* Bottom Navigation Bar - Crown Coins inspired with elevated center logo */}
    {(() => {
      const isGold = selectedCurrency === 'GC';
      const accentText = isGold ? 'text-amber-400' : 'text-emerald-400';
      const accentLine = isGold 
        ? 'from-transparent via-amber-500 to-transparent' 
        : 'from-transparent via-emerald-500 to-transparent';
      const logoBorderIdle = isGold ? 'border-amber-500' : 'border-emerald-500';
      const logoBorderActive = isGold 
        ? 'from-amber-500 to-amber-700 shadow-[0_0_20px_rgba(245,158,11,0.5)]' 
        : 'from-emerald-500 to-emerald-700 shadow-[0_0_20px_rgba(16,185,129,0.5)]';
      const logoGlow = isGold 
        ? 'shadow-[0_0_16px_rgba(245,158,11,0.15)]' 
        : 'shadow-[0_0_16px_rgba(16,185,129,0.15)]';
      const logoFilter = isGold 
        ? 'drop-shadow(0 0 4px rgba(245,158,11,0.3))' 
        : 'drop-shadow(0 0 4px rgba(16,185,129,0.3))';

      return (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-[250] mobile-bottom-nav bg-card shadow-2xl pointer-events-auto pb-[env(safe-area-inset-bottom)]">
          
          {/* Accent top line — gold or green based on currency */}
          <div className={`absolute top-0 left-0 right-0 h-[1.5px] bg-gradient-to-r ${accentLine}`} />

          {/* Nav items row */}
          <div className="relative flex justify-around items-center pt-2 pb-2 px-2">

            {/* Left nav items */}
            {leftNavItems.map((item, index) => {
              const isItemActive = item.path ? isActive(item.path) : false;
              return (
                <button
                  key={`left-${index}`}
                  onClick={item.action}
                  className={`relative flex flex-col items-center gap-1 px-2 py-1 h-auto min-w-0 flex-1 ${
                    isItemActive ? accentText : 'text-muted-foreground'
                  }`}
                >
                  <item.icon className={`h-7 w-7 ${isItemActive ? 'scale-110' : ''} transition-transform`} />
                  <span className={`text-[10px] font-medium ${isItemActive ? 'font-bold' : ''}`}>
                    {item.label}
                  </span>
                </button>
              );
            })}

            {/* Center Logo - elevated, sits on top of the line */}
            <button
              onClick={handleLogoClick}
              className="relative z-[3] flex flex-col items-center px-3 h-auto min-w-0 flex-1 -mt-7"
            >
              <div className={`relative w-[56px] h-[56px] rounded-full flex items-center justify-center transition-all duration-200 ${
                isQuickActionsOpen 
                  ? `bg-gradient-to-br ${logoBorderActive}` 
                  : `bg-card border-[2px] ${logoBorderIdle} ${logoGlow}`
              }`}>
                {isQuickActionsOpen ? (
                  <X className={`h-6 w-6 ${accentText}`} />
                ) : (
                  <img 
                    src={logoSrc} 
                    alt="Gold Machine" 
                    className="h-9 w-auto object-contain"
                    loading="eager"
                    decoding="sync"
                    style={{ filter: logoFilter }}
                  />
                )}
                {/* Overall notification count (chat + daily bonus, etc.) */}
                {totalNotificationCount > 0 && !isQuickActionsOpen && (
                  <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold leading-none shadow-sm">
                    {totalNotificationCount > 99 ? '99+' : totalNotificationCount}
                  </span>
                )}
              </div>
              <span className={`text-[10px] font-medium mt-0.5 ${isQuickActionsOpen ? accentText : 'text-muted-foreground'}`}>
                {isQuickActionsOpen ? 'Close' : 'Quick'}
              </span>
            </button>

            {/* Right nav items */}
            {rightNavItems.map((item, index) => {
              const isItemActive = item.path ? isActive(item.path) : false;
              const isMenuActive = item.label === "Menu" && isMenuOpen;
              const active = isItemActive || isMenuActive;
              return (
                <button
                  key={`right-${index}`}
                  onClick={item.action}
                  className={`relative flex flex-col items-center gap-1 px-2 py-1 h-auto min-w-0 flex-1 ${
                    active ? accentText : 'text-muted-foreground'
                  }`}
                >
                  {item.icon && (
                    <item.icon className={`h-7 w-7 ${active ? 'scale-110' : ''} transition-transform`} />
                  )}
                  <span className={`text-[10px] font-medium ${active ? 'font-bold' : ''}`}>
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      );
    })()}

    {/* Native App-Style Bottom Sheet Menu - sits above bottom nav */}
    {isMenuOpen && (
      <>
        {/* Backdrop - covers screen above bottom nav */}
        <div 
          className={`lg:hidden fixed inset-0 z-[240] bg-black/60 ${isMenuClosing ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
          style={{ 
            bottom: 'calc(64px + env(safe-area-inset-bottom, 0px))',
            opacity: menuDragY > 0 ? Math.max(0, 1 - menuDragY / 300) : undefined,
          }}
          onClick={closeMenu}
        />
        
        {/* Sheet - full height, stops above bottom nav */}
        <div
          ref={menuSheetRef}
          className={`lg:hidden fixed left-0 right-0 top-0 z-[241] bg-background menu-sheet-panel flex flex-col ${
            isDragClosing
              ? ''
              : isMenuClosing && !isDragClosing
                ? 'menu-sheet-exit'
                : isMenuEntered
                  ? ''
                  : 'menu-sheet-enter'
          }`}
          style={{
            transform: isDragClosing
              ? 'translateY(100%)'
              : menuDragY > 0
                ? `translateY(${menuDragY}px)`
                : isMenuEntered
                  ? 'translateY(0)'
                  : undefined,
            opacity: isDragClosing ? 0 : menuDragY > 0 ? Math.max(0.6, 1 - menuDragY / 400) : undefined,
            transition: isMenuDragging
              ? 'none'
              : isDragClosing
                ? 'transform 0.3s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.3s cubic-bezier(0.32, 0.72, 0, 1)'
                : 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          {/* Drag Handle */}
          <div 
            className="flex-shrink-0 flex justify-center pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] pb-2 touch-none cursor-grab active:cursor-grabbing select-none"
            onTouchStart={handleMenuDragStart}
            onTouchMove={handleMenuDragMove}
            onTouchEnd={handleMenuDragEnd}
          >
            <div 
              className="w-10 h-1.5 bg-muted-foreground/50 rounded-full transition-all duration-200"
              style={{
                opacity: isMenuDragging ? 0.6 : 1,
                transform: isMenuDragging ? 'scaleX(0.6)' : 'scaleX(1)',
                backgroundColor: isMenuDragging ? 'rgba(128, 128, 128, 0.7)' : undefined,
                transition: isMenuDragging ? 'none' : 'all 0.2s ease',
              }}
            />
          </div>

          {/* Close button */}
          <div className="absolute right-3 z-20" style={{ top: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}>
            <button
              onClick={closeMenu}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-foreground/10 active:bg-foreground/20 transition-colors"
              aria-label="Close menu"
            >
              <X className="h-4 w-4 text-foreground/60" />
            </button>
          </div>

          {/* Menu Content - flex child fills remaining space, scrolls independently */}
          <div 
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
          >
            <CasinoSidebarNew 
              isMobileFullScreen={true}
              onNavigate={closeMenuInstant}
              onGetCoinsClick={() => {
                closeMenuInstant();
                requestAnimationFrame(() => openGetCoinsModal());
              }}
              onRedeemClick={() => {
                closeMenuInstant();
                requestAnimationFrame(() => setIsRedeemModalOpen(true));
              }}
              onDailyBonusClick={() => {
                closeMenuInstant();
                requestAnimationFrame(() => openDailyBonusModal());
              }}
            />
          </div>
        </div>
      </>
    )}

    {/* Redeem Modal */}
    <RedeemModal 
      isOpen={isRedeemModalOpen} 
      onClose={() => {
        setIsRedeemModalOpen(false);
      }} 
    />
    </>
  );
};